import { memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useCanvasOffsets } from "@circle-react-shared/BuilderUI/CanvasTools/useCanvasOffsets";
import type { ComponentMeta } from "../BuilderUI.types";
import type { BaseBlock } from "../DataModel.types";
import { getBlockName } from "../Helpers/getBlockName";
import { useBuilderStore } from "../Hooks/useBuilderStore";

export interface PreviewBlockHoverProps {
  block: BaseBlock;
  blockMeta: ComponentMeta;
  hoveredElement: HTMLElement;
}

function calculatePosition(
  hoveredElement: HTMLElement,
  offsetX: number,
  offsetY: number,
): {
  left: number;
  top: number;
  width: number;
  height: number;
} {
  const hoveredRect = hoveredElement.getBoundingClientRect();

  return {
    left: hoveredRect.left + offsetX,
    top: hoveredRect.top + offsetY,
    width: hoveredRect.width,
    height: hoveredRect.height,
  };
}

const TOOLBAR_HEIGHT = 32;
const TOP_OFFSET = -31;
const BOTTOM_OFFSET = 7;

const calculateTopOffset = (
  position: ReturnType<typeof calculatePosition>,
  showInsideWhenFirstBlock: boolean,
): number => {
  if (showInsideWhenFirstBlock) {
    const elementTop = position.top;
    return elementTop < TOOLBAR_HEIGHT ? BOTTOM_OFFSET : TOP_OFFSET;
  }

  return TOP_OFFSET;
};

export const PreviewBlockHover = memo(
  ({ block, blockMeta, hoveredElement }: PreviewBlockHoverProps) => {
    const { shouldShowCanvasToolsInsideFirstBlock } = useBuilderStore(
      state => ({
        shouldShowCanvasToolsInsideFirstBlock:
          state.shouldShowCanvasToolsInsideFirstBlock,
      }),
    );

    const { offsetX, offsetY } = useCanvasOffsets();

    const [position, setPosition] = useState(() =>
      calculatePosition(hoveredElement, offsetX, offsetY),
    );

    useEffect(() => {
      const updatePosition = () => {
        setPosition(calculatePosition(hoveredElement, offsetX, offsetY));
      };

      const canvasContainer = document.getElementById("builder-ui-canvas");

      window.addEventListener("resize", updatePosition, { passive: true });

      if (canvasContainer) {
        canvasContainer.addEventListener("scroll", updatePosition, {
          passive: true,
        });
      }

      return () => {
        window.removeEventListener("resize", updatePosition);
        if (canvasContainer) {
          canvasContainer.removeEventListener("scroll", updatePosition);
        }
      };
    }, [hoveredElement]);

    if (!position) return null;

    const hoverElement = (
      <div
        className="pointer-events-none absolute box-border size-full rounded-none border-2 border-[#506CF0]"
        style={{
          left: position.left,
          top: position.top,
          width: position.width,
          height: position.height,
        }}
      >
        <div
          className="pointer-events-none absolute left-[-2px] rounded bg-[#506CF0] px-3 py-1 text-[0.688rem] font-semibold uppercase leading-4 tracking-wide text-white"
          style={{
            top: calculateTopOffset(
              position,
              shouldShowCanvasToolsInsideFirstBlock,
            ),
          }}
        >
          {getBlockName({
            block,
            meta: blockMeta,
          })}
        </div>
      </div>
    );

    const canvasCard = document.getElementById("builder-ui-canvas-card");
    if (!canvasCard) return null;

    return createPortal(hoverElement, canvasCard);
  },
);

PreviewBlockHover.displayName = "PreviewBlockHover";
